import { GeofenceHysteresis, inBoundary, newId, type PositionFix, type PropertyBoundary, type Trail } from '@cht/shared'
import type { FieldClient, FieldSettings } from '../api/field-client.js'
import type { GeoFix, GeolocationCapability } from '../platform/geolocation.js'
import { AmbientRecorder, type AmbientPositionStore } from './ambient-recorder.js'
import { TrailRecorder, type TrailDraft, type TrailDraftStore } from './trail-recorder.js'

export type FenceState = 'checking' | 'inside' | 'outside'
export type TrailMode = 'idle' | 'recording' | 'paused' | 'review'
export interface FieldSnapshot {
  tracking: boolean
  fence: FenceState
  fix: GeoFix | undefined
  error: string | undefined
  trailMode: TrailMode
  trailDraft: TrailDraft | undefined
  trails: Trail[]
  lastSyncAt?: number
  trailIssue: 'poor-accuracy' | 'outside-property' | undefined
}

type Listener = (snapshot: FieldSnapshot) => void
type TrailStore = TrailDraftStore & { delete(id: string): Promise<unknown>; toArray?(): Promise<TrailDraft[]> }

export class FieldSession {
  private readonly listeners = new Set<Listener>()
  private state: FieldSnapshot = { tracking: false, fence: 'checking', fix: undefined, error: undefined, trailMode: 'idle', trailDraft: undefined, trails: [], trailIssue: undefined }
  private stopWatch: (() => void) | undefined
  private ambient: AmbientRecorder | undefined
  private trailRecorder: TrailRecorder | undefined
  private fence: GeofenceHysteresis | undefined
  private pending = Promise.resolve()

  constructor(
    /** Every fix, draft and upload this session produces belongs to this property. */
    private readonly propertyId: string,
    private readonly geolocation: GeolocationCapability,
    private readonly boundary: PropertyBoundary,
    private readonly settings: FieldSettings,
    private readonly positions: AmbientPositionStore,
    private readonly trailDrafts: TrailStore,
    private readonly api: FieldClient
  ) {}

  snapshot(): FieldSnapshot { return structuredClone(this.state) }
  subscribe(listener: Listener): () => void { this.listeners.add(listener); listener(this.snapshot()); return () => this.listeners.delete(listener) }
  async settled(): Promise<void> { await this.pending }

  async loadTrails(): Promise<void> {
    this.state.trails = await this.api.trails()
    this.emit()
  }

  startTracking(userId: string): void {
    if (this.stopWatch) return
    this.fence = new GeofenceHysteresis(this.settings.geofenceConsecutiveFixes)
    this.ambient = new AmbientRecorder({ put: async (fix) => {
      await this.positions.put(fix)
      try {
        await this.api.sendPosition(fix)
        this.state.lastSyncAt = Date.now()
      } catch { /* The local fix remains available for later sync. */ }
    } }, this.boundary, this.settings.geofenceBufferMeters, this.propertyId, userId, newId)
    this.state = { ...this.state, tracking: true, fence: 'checking', error: undefined }
    this.stopWatch = this.geolocation.watch(
      (fix) => { this.pending = this.pending.then(() => this.processFix(fix)) },
      (error) => {
        this.stopWatch?.()
        this.stopWatch = undefined
        this.ambient = undefined
        this.fence = undefined
        this.state = { ...this.state, tracking: false, fence: 'checking', fix: undefined, error: error.message }
        this.emit()
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 }
    )
    this.emit()
  }

  stopTracking(): void {
    this.stopWatch?.()
    this.stopWatch = undefined
    this.ambient = undefined
    this.fence = undefined
    this.state = { ...this.state, tracking: false, fence: 'checking', fix: undefined, error: undefined }
    if (this.state.trailMode === 'recording' || this.state.trailMode === 'paused') this.finishTrail()
    this.emit()
  }

  startTrail(authorId: string, now = Date.now()): void {
    if (!this.state.tracking) throw new Error('Turn on GPS before recording a trail')
    if (this.state.fence !== 'inside') throw new Error('Walk onto the property before recording a trail')
    this.trailRecorder = new TrailRecorder(this.trailDrafts, this.boundary, {
      maxAccuracyMeters: this.settings.trailMaxAccuracyMeters,
      minimumLengthMeters: this.settings.trailMinimumLengthMeters,
      simplifyTolerance: this.settings.trailSimplifyTolerance,
      geofenceBufferMeters: this.settings.geofenceBufferMeters,
      sampleIntervalMs: this.settings.trailSampleIntervalMs,
      sampleDistanceMeters: this.settings.trailSampleDistanceMeters
    })
    this.state.trailDraft = this.trailRecorder.start(newId(), this.propertyId, authorId, now)
    this.state.trailMode = 'recording'
    this.state.trailIssue = undefined
    this.emit()
  }

  pauseTrail(): void { this.trailRecorder?.pause(); this.state.trailMode = 'paused'; this.emit() }
  resumeTrail(): void { this.trailRecorder?.resume(); this.state.trailMode = 'recording'; this.state.trailIssue = undefined; this.emit() }
  finishTrail(): void {
    if (!this.trailRecorder) return
    this.state.trailDraft = this.trailRecorder.snapshot()
    this.state.trailMode = 'review'
    this.emit()
  }

  async discardTrail(): Promise<void> {
    if (this.state.trailDraft) await this.trailDrafts.delete(this.state.trailDraft.id)
    this.trailRecorder = undefined
    this.state.trailDraft = undefined
    this.state.trailMode = 'idle'
    this.state.trailIssue = undefined
    this.emit()
  }

  async recoverTrailDraft(authorId: string): Promise<void> {
    const drafts = await this.trailDrafts.toArray?.() ?? []
    const draft = drafts.filter((candidate) => candidate.propertyId === this.propertyId && candidate.authorId === authorId)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0]
    if (!draft) return
    const serverTrail = this.state.trails.find((trail) => trail.id === draft.id)
    if (serverTrail?.status === 'published') { await this.trailDrafts.delete(draft.id); return }
    const recovered: TrailDraft = {
      ...draft,
      trimmedTail: draft.trimmedTail ?? [],
      syncState: serverTrail ? 'server' : (draft.syncState ?? 'device'),
      serverCreated: Boolean(serverTrail || draft.serverCreated)
    }
    this.trailRecorder = new TrailRecorder(this.trailDrafts, this.boundary, {
      maxAccuracyMeters: this.settings.trailMaxAccuracyMeters,
      minimumLengthMeters: this.settings.trailMinimumLengthMeters,
      simplifyTolerance: this.settings.trailSimplifyTolerance,
      geofenceBufferMeters: this.settings.geofenceBufferMeters,
      sampleIntervalMs: this.settings.trailSampleIntervalMs,
      sampleDistanceMeters: this.settings.trailSampleDistanceMeters
    })
    this.state.trailDraft = this.trailRecorder.restore(recovered)
    this.state.trailMode = 'review'; this.state.trailIssue = undefined
    this.emit()
  }

  async trimTrail(): Promise<void> {
    if (!this.trailRecorder) return
    this.state.trailDraft = await this.trailRecorder.trimLastPoint()
    this.emit()
  }

  async undoTrailTrim(): Promise<void> {
    if (!this.trailRecorder) return
    this.state.trailDraft = await this.trailRecorder.undoTrim()
    this.emit()
  }

  async saveTrailDraft(name: string, description: string): Promise<void> {
    if (!this.trailRecorder || !this.state.trailDraft) throw new Error('No trail draft to save')
    if (!name.trim()) throw new Error('Trail name is required')
    const geometry = this.trailRecorder.geometry()
    const serverCreated = this.state.trailDraft.serverCreated
    this.state.trailDraft = await this.trailRecorder.markSync(name.trim(), description.trim(), 'syncing', serverCreated)
    this.emit()
    try {
      const request = { id: this.state.trailDraft.id, name: name.trim(), description: description.trim(), geometry, status: 'draft' as const }
      if (this.state.trailDraft.serverCreated) await this.api.updateTrail(request)
      else await this.api.createTrail(request)
      this.state.trailDraft = await this.trailRecorder.markSync(name.trim(), description.trim(), 'server', true)
      this.emit()
    } catch (error) {
      this.state.trailDraft = await this.trailRecorder.markSync(name.trim(), description.trim(), 'device', serverCreated)
      this.emit()
      throw error
    }
  }

  async publishTrail(name: string, description: string): Promise<void> {
    if (!this.trailRecorder || !this.state.trailDraft) throw new Error('No trail draft to publish')
    const geometry = this.trailRecorder.publish(name)
    if (this.state.trailDraft.serverCreated) {
      await this.api.updateTrail({ id: this.state.trailDraft.id, name: name.trim(), description: description.trim(), geometry, status: 'published' })
    } else {
      await this.api.createTrail({ id: this.state.trailDraft.id, name: name.trim(), description: description.trim(), geometry, status: 'published' })
    }
    await this.trailDrafts.delete(this.state.trailDraft.id)
    this.trailRecorder = undefined
    this.state.trailDraft = undefined
    this.state.trailMode = 'idle'
    this.state.trailIssue = undefined
    await this.loadTrails()
  }

  private async processFix(fix: GeoFix): Promise<void> {
    const rawInside = inBoundary(fix, this.boundary, this.settings.geofenceBufferMeters)
    const fence = this.fence!.observe(rawInside).inside
    this.state.fix = fix
    this.state.fence = fence === undefined ? 'checking' : fence ? 'inside' : 'outside'
    if (fence) {
      await this.ambient?.accept(fix)
      if (this.state.trailMode === 'recording') {
        const accepted = await this.trailRecorder?.accept(fix)
        this.state.trailIssue = !accepted && fix.accuracyMeters > this.settings.trailMaxAccuracyMeters ? 'poor-accuracy' : undefined
        this.state.trailDraft = this.trailRecorder?.snapshot()
      }
    } else if (this.state.trailMode === 'recording' && fence === false) {
      this.state.trailDraft = await this.trailRecorder?.reject(fix.recordedAt)
      this.trailRecorder?.pause()
      this.state.trailMode = 'paused'
      this.state.trailIssue = 'outside-property'
    }
    this.emit()
  }

  private emit(): void { for (const listener of this.listeners) listener(this.snapshot()) }
}
